import React, { useMemo, useState, useEffect, isValidElement, Children } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";

/**
 * Renders a model's answer as GitHub-flavoured Markdown with a hand-tuned
 * dark theme. Handles the things AI models actually emit today:
 *   • Markdown images and raw <img> tags
 *   • Tables, task lists, strikethrough (via GFM)
 *   • Code blocks (block + inline)
 *   • Fenced ```svg blocks are rendered as ACTUAL images (users see the logo,
 *     not the source). A collapsible "View SVG source" details sits below.
 *   • Links (open in a new tab)
 *
 * We're on react-markdown v10 which:
 *   – no longer passes `inline` to <code>, so inline vs block must be inferred
 *     from parent (we handle blocks via a <pre> override);
 *   – passes `children` as React element(s), so we flatten before parsing.
 */

/* Sanitize schema — extends the default with img, table, and safe data: URLs */
const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames || []),
    "img", "figure", "figcaption", "picture", "source",
    "details", "summary", "mark", "abbr", "kbd", "sub", "sup",
  ],
  attributes: {
    ...(defaultSchema.attributes || {}),
    img: ["src", "alt", "title", "width", "height", "loading"],
    a: [...((defaultSchema.attributes || {}).a || []), "target", "rel", "title"],
    "*": [
      ...((defaultSchema.attributes || {})["*"] || []),
      "className", "style",
    ],
  },
  protocols: {
    ...(defaultSchema.protocols || {}),
    src: ["http", "https", "data"],
    href: ["http", "https", "mailto", "tel"],
  },
};

/** Recursively collect any text content from React children. */
function flattenText(node) {
  if (node == null || node === false) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(flattenText).join("");
  if (isValidElement(node)) return flattenText(node.props?.children);
  return "";
}

/** Unicode-safe base64 encoder for data URLs. */
function toBase64(str) {
  // eslint-disable-next-line no-undef
  return btoa(unescape(encodeURIComponent(str)));
}

/**
 * If the root <svg> element is missing the xmlns attribute (some models —
 * looking at you, Grok — omit it), inject it. Without xmlns the browser
 * refuses to decode the SVG when it's set as an <img src>.
 */
function ensureSvgNamespace(svg) {
  if (/<svg\b[^>]*xmlns\s*=/i.test(svg)) return svg;
  return svg.replace(/<svg\b/i, '<svg xmlns="http://www.w3.org/2000/svg"');
}

const components = {
  a: (props) => <a {...props} target="_blank" rel="noopener noreferrer" />,

  img: ({ src, alt, ...rest }) => (
    <img
      src={src}
      alt={alt || ""}
      loading="lazy"
      className="pq-md-img"
      {...rest}
    />
  ),

  // Inline code only. Block code is handled by <pre> below.
  code: ({ className, children, ...props }) => (
    <code className={className || "pq-md-inline-code"} {...props}>
      {children}
    </code>
  ),

  // Block code — react-markdown wraps fenced blocks as <pre><code class="language-x">…</code></pre>.
  pre: ({ children }) => {
    // Find the single <code> child.
    const codeEl = Children.toArray(children).find(
      (c) => isValidElement(c) && (c.type === "code" || c.props?.className?.startsWith?.("language-"))
    );
    const className = codeEl?.props?.className || "";
    const raw = flattenText(codeEl?.props?.children).replace(/\n$/, "");
    const lang = /language-(\w+)/.exec(className)?.[1];
    const trimmed = raw.trim();
    const isSvg =
      (lang && lang.toLowerCase() === "svg") || trimmed.startsWith("<svg");

    // Only render as an image once the SVG is complete. During streaming,
    // partial SVG chunks (no closing tag yet) would produce invalid base64
    // data URLs and fire onError — latching the fallback state permanently.
    // Fall through to the plain code-block until </svg> arrives.
    const svgComplete = /<\/svg\s*>/i.test(trimmed);

    if (isSvg && svgComplete) {
      const normalized = ensureSvgNamespace(trimmed);
      const dataUrl = `data:image/svg+xml;base64,${toBase64(normalized)}`;
      return <SvgFigure src={dataUrl} raw={raw} />;
    }

    return (
      <pre className="pq-md-code thin-scroll">
        {lang && <div className="pq-md-code-lang">{lang}</div>}
        <code className={className}>{raw}</code>
      </pre>
    );
  },

  table: (props) => (
    <div className="pq-md-table-wrap thin-scroll">
      <table {...props} />
    </div>
  ),
};

/**
 * SVG figure with an onError fallback: if the browser can't decode the
 * data-URL image (truly malformed SVG from the model), we swap to a
 * friendly placeholder alongside the raw source so users still see it.
 */
function SvgFigure({ src, raw }) {
  const [failed, setFailed] = useState(false);
  // Reset the failure latch whenever the src changes so that even if an
  // early stream tick fires onError, a later valid src still renders.
  useEffect(() => { setFailed(false); }, [src]);
  return (
    <figure className="pq-md-svg-figure">
      {!failed ? (
        <img
          src={src}
          alt="SVG rendered from the model's answer"
          loading="lazy"
          className="pq-md-img pq-md-svg"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="pq-md-svg-fallback">
          <span className="label-eyebrow">malformed svg · source below</span>
        </div>
      )}
      <details className="pq-md-svg-details" {...(failed ? { open: true } : {})}>
        <summary>View SVG source</summary>
        <pre className="pq-md-code thin-scroll">
          <code>{raw}</code>
        </pre>
      </details>
    </figure>
  );
}

export default function MarkdownAnswer({ text }) {
  const rendered = useMemo(() => {
    if (!text) return null;
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
        components={components}
      >
        {text}
      </ReactMarkdown>
    );
  }, [text]);

  return <div className="pq-md">{rendered}</div>;
}
